import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DateFilter from "./DateFilter";
import Pagination from "./Pagination";

const TH = ["Customer", "Email", "Plan", "Amount / mo", "Interval", "Canceled At", "Subscribed Since"];

const toUnix = (d, endOfDay) => {
  if (!d) return null;
  const dt = new Date(d);
  if (endOfDay) dt.setHours(23, 59, 59, 999);
  return Math.floor(dt.getTime() / 1000);
};

function SkeletonRows() {
  return Array.from({ length: 6 }).map((_, i) => (
    <tr key={i}>
      {TH.map((_, j) => (
        <td key={j} style={{ padding: "13px 14px" }}>
          <div className="stripe-skeleton" style={{ height: 13, borderRadius: 4, width: j === 0 ? "70%" : "60%" }} />
        </td>
      ))}
    </tr>
  ));
}

export default function ChurnedTab() {
  const [churned, setChurned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState([]);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const buildUrl = useCallback((cursor, start, end) => {
    let url = "/api/stripe/subscriptions/churned?limit=50";
    const s = toUnix(start, false);
    const e = toUnix(end, true);
    if (s) url += `&startDate=${s}`;
    if (e) url += `&endDate=${e}`;
    if (cursor) url += `&starting_after=${cursor}`;
    return url;
  }, []);

  const fetchPage = useCallback((cursor, start, end) => {
    setLoading(true);
    axios
      .get(buildUrl(cursor, start, end))
      .then((res) => {
        if (res.data.success) {
          setChurned(res.data.data);
          setHasMore(res.data.hasMore);
        }
      })
      .finally(() => setLoading(false));
  }, [buildUrl]);

  useEffect(() => { fetchPage(undefined, "", ""); }, [fetchPage]);

  const handleApply = () => {
    setCursorStack([]); setPage(1);
    fetchPage(undefined, startDate, endDate);
  };
  const handleClear = () => {
    setStartDate(""); setEndDate("");
    setCursorStack([]); setPage(1);
    fetchPage(undefined, "", "");
  };
  const handleNext = () => {
    const lastId = churned[churned.length - 1]?.id;
    if (!lastId) return;
    setCursorStack((p) => [...p, lastId]);
    setPage((p) => p + 1);
    fetchPage(lastId, startDate, endDate);
  };
  const handlePrev = () => {
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    setPage((p) => p - 1);
    fetchPage(newStack[newStack.length - 1], startDate, endDate);
  };

  return (
    <div className="stripe-section-card">
      <div className="stripe-section-header">
        <div>
          <span className="stripe-section-title">Churned Subscriptions</span>
          {!loading && <span className="stripe-section-count">{churned.length} on this page</span>}
        </div>
        <DateFilter
          startDate={startDate} endDate={endDate}
          onStartChange={setStartDate} onEndChange={setEndDate}
          onApply={handleApply} onClear={handleClear}
        />
      </div>

      <div className="scroll-x-container" style={{ border: "none", borderRadius: 0 }}>
        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>{TH.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows /> : churned.length === 0 ? (
                <tr>
                  <td colSpan={TH.length}>
                    <div className="stripe-empty">
                      <div className="stripe-empty-icon">✅</div>
                      No churned subscriptions — great retention!
                    </div>
                  </td>
                </tr>
              ) : churned.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 700 }}>{s.customerName}</td>
                  <td style={{ color: "#6b7280" }}>{s.customerEmail || "—"}</td>
                  <td>{s.planName}</td>
                  <td style={{ fontWeight: 700, color: "#6b7280" }}>
                    ${s.amount.toFixed(2)}
                  </td>
                  <td>
                    <span className="stripe-badge" style={{ background: "#f3f4f6", color: "#374151" }}>
                      {s.interval}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: "#dc2626", fontWeight: 700 }}>
                      {s.canceledAt ? new Date(s.canceledAt * 1000).toLocaleDateString() : "—"}
                    </span>
                  </td>
                  <td style={{ color: "#6b7280" }}>
                    {new Date(s.createdAt * 1000).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} hasMore={hasMore} onPrev={handlePrev} onNext={handleNext} loading={loading} />
    </div>
  );
}
